import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTimer } from '../hooks/useTimer';
import Sidebar from './Sidebar/Sidebar';
import TopNav from './TopNav/TopNav';
import PatientHeader from './PatientHeader/PatientHeader';
import ChatContainer from './Chat/ChatContainer';
import ChatInput from './Chat/ChatInput';
import EvaluationModal from './Modals/EvaluationModal';
import ImageModal from './Modals/ImageModal';
import TimerModal from './Modals/TimerModal';
import LabReportsPanel from './LabReports/LabReportsPanel';
import AnketaPanel from './Anketa/AnketaPanel';
import { apiService, getPatientImageUrl } from '../services/api';
import { getCaseProfile, getLocalizedCaseDisplay } from '../data/caseProfiles';
import { useLanguage } from '../context/LanguageContext';

const CASES_CACHE_KEY = 'medsim-cases-cache';
const TRANSCRIPT_BACKUP_KEY = 'medsim-transcript-backups';

const DEFAULT_PHASES = [
    { name: "Anamnesis", minutes: 6 },
    { name: "Physical Exam", minutes: 5 },
    { name: "Differential & Tests", minutes: 5 },
    { name: "Evaluation & Management", minutes: 10 }
];

const compilePhases = (phases) => {
    let cumulative = 0;
    return phases.map((phase) => {
        const duration = Number(phase.minutes) * 60 * 1000;
        cumulative += duration;
        return { ...phase, duration, cumulative };
    });
};

const DEFAULT_COMPILED_PHASES = compilePhases(DEFAULT_PHASES);
const getTotalDuration = (phases) => phases.reduce((total, phase) => total + phase.duration, 0);
const isMobileViewport = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

const loadTranscriptBackups = () => {
    try {
        return JSON.parse(localStorage.getItem(TRANSCRIPT_BACKUP_KEY) || '{}');
    } catch {
        return {};
    }
};

const saveTranscriptBackup = (caseId, submission) => {
    if (!caseId) return;
    const backups = loadTranscriptBackups();
    backups[caseId] = submission;
    localStorage.setItem(TRANSCRIPT_BACKUP_KEY, JSON.stringify(backups));
};

const buildTranscript = (messages) => messages
    .filter(message => typeof message.text === 'string' && message.text.trim() && (
        message.sender === 'user' || message.sender === 'ai' || message.transcriptRole === 'system'
    ))
    .map(message => ({
        role: message.transcriptRole || (message.sender === 'user' ? 'user' : 'patient'),
        content: message.text
    }));

function Dashboard() {
    const { user } = useAuth();
    const { language, t, phaseName } = useLanguage();
    const navigate = useNavigate();
    const [cases, setCases] = useState([]);
    const [casesLoading, setCasesLoading] = useState(true);
    const [selectedCase, setSelectedCase] = useState(null);
    const [tempSelectedCase, setTempSelectedCase] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(() => !isMobileViewport());
    const [showEvaluationForm, setShowEvaluationForm] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showTimerModal, setShowTimerModal] = useState(false);
    const [diagnosis, setDiagnosis] = useState('');
    const [managementPlan, setManagementPlan] = useState('');
    const [evaluationResult, setEvaluationResult] = useState(null);
    const [isEvaluating, setIsEvaluating] = useState(false);
    const [patientImage, setPatientImage] = useState('');
    const [imageZoom, setImageZoom] = useState(1);
    const [isTimerExpired, setIsTimerExpired] = useState(false);
    const [sessionActive, setSessionActive] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [showLabReports, setShowLabReports] = useState(false);
    const [showAnketaPanel, setShowAnketaPanel] = useState(false);

    useEffect(() => {
        if (user && user.role === 'doctor') {
            navigate('/doctor-review', { replace: true });
        }
    }, [user, navigate]);

    useEffect(() => {
        const handleResize = () => {
            setSidebarOpen(prev => (isMobileViewport() ? false : (prev || Boolean(selectedCase || sessionActive))));
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [selectedCase, sessionActive]);

    const [completedSubmissions, setCompletedSubmissions] = useState({});
    const [showCompletedModal, setShowCompletedModal] = useState(false);
    const [completedModalCase, setCompletedModalCase] = useState(null);
    const [viewingTranscript, setViewingTranscript] = useState(null);

    const inactivityMessagesByLanguage = {
        en: [
            "Are you still there?",
            "Is everything alright?",
            "Should I wait for your response?",
            "Hello? Are you listening to me?",
            "Do you need more time to think?"
        ],
        ru: [
            "Вы ещё здесь?",
            "Всё в порядке?",
            "Мне подождать вашего ответа?",
            "Здравствуйте, вы меня слышите?",
            "Вам нужно больше времени подумать?"
        ],
        uz: [
            "Hali shu yerdamisiz?",
            "Hammasi joyidami?",
            "Javobingizni kutaymi?",
            "Assalomu alaykum, meni eshityapsizmi?",
            "O'ylash uchun ko'proq vaqt kerakmi?"
        ]
    };
    const inactivityMessages = inactivityMessagesByLanguage[language] || inactivityMessagesByLanguage.en;

    const [timerPhases, setTimerPhases] = useState(DEFAULT_COMPILED_PHASES);
    const [sessionDuration, setSessionDuration] = useState(getTotalDuration(DEFAULT_COMPILED_PHASES));
    const timer = useTimer(handleTimerExpired, 5 * 60 * 1000);
    const [currentPhase, setCurrentPhase] = useState(timerPhases[0].name);
    const [phaseTimeRemaining, setPhaseTimeRemaining] = useState(timerPhases[0].duration);
    const formatPhaseNotice = (key, phase) => t(key)
        .replace('{phase}', phaseName(phase.name))
        .replace('{minutes}', String(Math.round(phase.duration / 60000)))
        .replace('{unit}', t('minutesShort'));

    useEffect(() => {
        if (!timer.isActive) return;

        const elapsedTime = sessionDuration - timer.timeLeft;
        const phase = timerPhases.find(p => elapsedTime < p.cumulative) || timerPhases[timerPhases.length - 1];

        // Calculate phase-specific time remaining
        const phaseStartTime = phase.cumulative - phase.duration;
        const phaseElapsed = elapsedTime - phaseStartTime;
        const phaseRemaining = Math.max(0, phase.duration - phaseElapsed);
        setPhaseTimeRemaining(phaseRemaining);

        if (phase.name !== currentPhase) {
            setCurrentPhase(phase.name);

            // Notify user of phase change
            const phaseMsg = {
                id: Date.now(),
                text: `🔔 ${formatPhaseNotice('phaseChangeStarting', phase)}`,
                sender: 'system',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, phaseMsg]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timer.timeLeft, timer.isActive, currentPhase, sessionDuration, timerPhases]);

    useEffect(() => {
        fetchMySubmissions();
    }, []);

    useEffect(() => {
        fetchCases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    const fetchMySubmissions = async () => {
        try {
            const subs = await apiService.getMySubmissions();
            if (Array.isArray(subs)) {
                const map = {};
                subs.forEach(s => {
                    if (!map[s.case_id] || new Date(s.created_at) > new Date(map[s.case_id].created_at)) {
                        map[s.case_id] = s;
                    }
                });
                setCompletedSubmissions(map);
            }
        } catch {
            // Not critical — just means no completion indicators shown
        }
    };

    function handleTimerExpired() {
        setIsTimerExpired(true);

        const expiryMessage = {
            id: messages.length + 1,
            text: `⏰ ${t('timeUpMessage')}`,
            sender: 'system',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, expiryMessage]);

        setTimeout(() => {
            setShowEvaluationForm(true);
        }, 1000);
    }

    function handleNextPhase() {
        if (!timer.isActive || timer.isExpired) return;

        const elapsedTime = sessionDuration - timer.timeLeft;
        const currentPhaseIndex = timerPhases.findIndex(p => elapsedTime < p.cumulative);

        // If we're already in the last phase, don't allow skipping
        if (currentPhaseIndex === -1 || currentPhaseIndex >= timerPhases.length - 1) {
            return;
        }

        // Calculate time to skip to reach the next phase
        const nextPhaseCumulative = timerPhases[currentPhaseIndex].cumulative;
        const timeToSkip = nextPhaseCumulative - elapsedTime;

        // Skip the remaining time of current phase
        timer.skipTime(timeToSkip);

        // Add notification message
        const nextPhase = timerPhases[currentPhaseIndex];
        const skipMessage = {
            id: Date.now(),
            text: `⏭️ ${formatPhaseNotice('phaseSkippedMoving', nextPhase)}`,
            sender: 'system',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages(prev => [...prev, skipMessage]);
    }

    function handleInactivityTimeout() {
        if (selectedCase && messages.length > 0 && timer.isActive && !timer.isExpired) {
            const randomMessage = inactivityMessages[Math.floor(Math.random() * inactivityMessages.length)];

            const aiResponse = {
                id: messages.length + 1,
                text: randomMessage,
                sender: 'ai',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };

            setMessages(prev => [...prev, aiResponse]);
            timer.resetInactivity();
        }
    }

    useEffect(() => {
        if (timer.shouldWarn) {
            handleInactivityTimeout();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timer.shouldWarn]);

    const fetchCases = async () => {
        setCasesLoading(true);
        const localizedCacheKey = `${CASES_CACHE_KEY}-${language}`;
        try {
            const cachedCases = localStorage.getItem(localizedCacheKey);
            if (cachedCases) {
                setCases(JSON.parse(cachedCases));
                setCasesLoading(false);
            }

            const result = await apiService.getCases(language);

            if (result.success) {
                const mappedCases = result.data.map(caseItem => ({
                    id: caseItem.id,
                    name: caseItem.name,
                    age: caseItem.age,
                    chiefComplaint: caseItem.chief_complaint || caseItem.chiefComplaint || 'No complaint specified'
                }));

                const filteredCases = mappedCases.filter(caseItem => {
                    const isExample = caseItem.name?.toLowerCase().includes('example') ||
                        caseItem.name?.includes('John Smith');
                    const hasValidComplaint = caseItem.chiefComplaint &&
                        caseItem.chiefComplaint !== 'undefined' &&
                        caseItem.chiefComplaint !== 'null';

                    return !isExample && hasValidComplaint;
                });

                setCases(filteredCases);
                localStorage.setItem(localizedCacheKey, JSON.stringify(filteredCases));
            }
        } catch (error) {
            console.error('Error fetching cases:', error);
        } finally {
            setCasesLoading(false);
        }
    };

    const handleSelectCase = async (caseItem) => {
        if (sessionActive) {
            alert("Current session is in progress. Please finish the current case or cancel the session to switch cases.");
            return;
        }

        if (isTimerExpired) {
            alert("Session expired! Please complete your evaluation before starting a new case.");
            return;
        }

        if (completedSubmissions[caseItem.id]) {
            setCompletedModalCase(caseItem);
            setShowCompletedModal(true);
            return;
        }

        setTempSelectedCase(caseItem);
        setPatientImage('');
        setShowTimerModal(true);
    };

    const handleRedoCase = () => {
        setShowCompletedModal(false);
        setTempSelectedCase(completedModalCase);
        setPatientImage('');
        setShowTimerModal(true);
    };

    const handleViewTranscript = () => {
        const currentTranscript = buildTranscript(messages);
        const saved = completedSubmissions[completedModalCase.id];
        const backup = loadTranscriptBackups()[completedModalCase.id];
        const usableSaved = saved && (saved.transcript || []).length > 0 ? saved : backup;
        const sub = usableSaved && (usableSaved.transcript || []).length > 0
            ? usableSaved
            : {
                ...(saved || {}),
                case_id: completedModalCase.id,
                transcript: currentTranscript,
                student_diagnosis: diagnosis,
                student_plan: managementPlan
            };
        if (sub.id && sub.system_result) {
            setShowCompletedModal(false);
            navigate(`/results/${sub.id}`, {
                state: {
                    submission: {
                        ...sub,
                        patient_name: sub.patient_name || completedModalCase.name
                    }
                }
            });
            return;
        }
        setViewingTranscript(sub);
        setShowCompletedModal(false);
    };

    const loadCaseData = (caseItem) => {
        setMessages([]);
        setEvaluationResult(null);
        setDiagnosis('');
        setManagementPlan('');
        setShowEvaluationForm(false);
        setShowImageModal(false);
        setPatientImage('');
        setShowLabReports(false);
        setShowAnketaPanel(false);

        const profile = getCaseProfile(caseItem);
        const localizedCase = getLocalizedCaseDisplay(caseItem, language);
        const initialMessage = {
            id: 1,
            sender: 'system',
            type: 'chart',
            chart: {
                title: t('medicalCaseAnketa'),
                caseId: localizedCase.id,
                patient: localizedCase.name,
                age: localizedCase.age,
                chiefComplaint: localizedCase.chiefComplaint,
                ...profile.initialChart,
                triage: localizedCase.triage || profile.initialChart.triage,
                demographics: {
                    ...profile.demographics,
                    sex: localizedCase.gender || profile.demographics.sex,
                    setting: localizedCase.setting || profile.demographics.setting
                }
            },
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
        setMessages([initialMessage]);

        const imageUrl = getPatientImageUrl(caseItem.id);
        const testImage = new Image();
        testImage.onload = () => {
            setPatientImage(imageUrl);
        };
        testImage.onerror = () => {
            setPatientImage('');
        };
        testImage.src = imageUrl;
    };

    const handleStartSession = async (configuredPhases = DEFAULT_PHASES) => {
        console.log("Starting session...", { tempSelectedCase });
        const nextPhases = compilePhases(configuredPhases);
        const nextDuration = getTotalDuration(nextPhases);
        setTimerPhases(nextPhases);
        setSessionDuration(nextDuration);
        setCurrentPhase(nextPhases[0].name);
        setPhaseTimeRemaining(nextPhases[0].duration);
        timer.start(nextDuration);
        setSessionActive(true);
        setShowTimerModal(false);

        if (tempSelectedCase) {
            console.log("Setting selected case:", tempSelectedCase.id);
            setSelectedCase(tempSelectedCase);
            loadCaseData(tempSelectedCase);

            try {
                const result = await apiService.startSession(tempSelectedCase.id);
                console.log("Session start result:", result);
                if (result.success && result.data.sessionId) {
                    setSessionId(result.data.sessionId);
                }
            } catch (error) {
                console.error("Failed to start session on backend:", error);
            }

            setTempSelectedCase(null);
        } else {
            console.error("CRITICAL: tempSelectedCase is null in handleStartSession!");
        }
    };

    const handleSend = async (e) => {
        e.preventDefault();

        if (!inputText.trim() || !selectedCase) return;

        if (isTimerExpired) {
            alert("Session expired! Please complete your evaluation.");
            setShowEvaluationForm(true);
            return;
        }

        const userMessage = {
            id: messages.length + 1,
            text: inputText,
            sender: 'user',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => [...prev, userMessage]);
        setInputText('');
        setIsTyping(true);

        try {
            timer.resetInactivity();
            const transcript = buildTranscript([...messages, userMessage]);
            const result = await apiService.sendChatMessage(
                selectedCase.id,
                inputText,
                currentPhase,
                transcript,
                language
            );

            if (result.success) {
                const aiResponse = {
                    id: messages.length + 2,
                    text: result.data.response,
                    sender: result.data.responder === 'system' ? 'system' : 'ai',
                    transcriptRole: result.data.responder === 'system' ? 'system' : 'patient',
                    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                };
                setMessages(prev => [...prev, aiResponse]);
            }
        } catch (error) {
            console.error('Error sending message:', error);

            let errorMessage = t('processingError');

            if (error.message === 'Authentication expired') {
                errorMessage = t('authExpired');
            }

            const errorResponse = {
                id: messages.length + 2,
                text: `⚠️ ${errorMessage}`,
                sender: 'system',
                timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, errorResponse]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleSubmitEvaluation = async () => {
        if (!diagnosis.trim() || !managementPlan.trim() || !selectedCase) {
            alert(t('fillBoth'));
            return;
        }

        setIsEvaluating(true);
        try {
            const transcript = buildTranscript(messages);

            const result = await apiService.evaluateDiagnosis(
                selectedCase.id,
                diagnosis,
                managementPlan,
                transcript,
                language
            );

            if (result.success) {
                const savedSubmission = {
                    id: result.data?.submission_id,
                    case_id: selectedCase.id,
                    patient_name: selectedCase.name,
                    created_at: new Date().toISOString(),
                    system_score: result.data?.total_score ?? result.data?.score,
                    system_result: result.data,
                    language: result.data?.language || language,
                    transcript,
                    student_diagnosis: diagnosis,
                    student_plan: managementPlan
                };
                saveTranscriptBackup(selectedCase.id, savedSubmission);
                setCompletedSubmissions(prev => ({
                    ...prev,
                    [selectedCase.id]: savedSubmission
                }));
                fetchMySubmissions();
                setIsTimerExpired(false);
                setSessionActive(false);
                setSessionId(null);
                timer.reset();
                setShowEvaluationForm(false);
                navigate(`/results/${result.data.submission_id}`, {
                    state: { submission: savedSubmission }
                });
            }
        } catch (error) {
            console.error('Error evaluating diagnosis:', error);
            if (error.message === 'Authentication expired') {
                alert(t('authExpiredRelogin'));
            } else {
                alert(t('evaluationSubmitError'));
            }
        } finally {
            setIsEvaluating(false);
        }
    };

    const handleClearChat = () => {
        setSelectedCase(null);
        setTempSelectedCase(null);
        setMessages([]);
        setEvaluationResult(null);
        setDiagnosis('');
        setManagementPlan('');
        setShowEvaluationForm(false);
        setShowImageModal(false);
        setShowTimerModal(false);
        setPatientImage('');
        setImageZoom(1);
        setIsTimerExpired(false);
        setSessionActive(false);
        setSessionId(null);
        setShowLabReports(false);
        timer.reset();
    };

    const handleReturnToDashboard = () => {
        if (sessionActive && selectedCase) {
            const confirmLeave = window.confirm(t('returnDashboardConfirm'));
            if (!confirmLeave) return;
        }
        handleClearChat();
    };

    const handleQuickQuestion = (question) => {
        if (isTimerExpired) {
            alert(t('mustCompleteEval'));
            setShowEvaluationForm(true);
            return;
        }
        setInputText(question);
    };

    const resetImageZoom = () => {
        setImageZoom(1);
    };

    const zoomIn = () => {
        setImageZoom(prev => Math.min(prev + 0.2, 3));
    };

    const zoomOut = () => {
        setImageZoom(prev => Math.max(prev - 0.2, 0.5));
    };

    const handleEvaluateClick = () => {
        if (isTimerExpired) {
            setShowEvaluationForm(true);
        } else if (timer.timeLeft > 0) {
            const confirm = window.confirm(t('goEvalConfirm'));
            if (confirm) {
                setShowEvaluationForm(true);
            }
        } else {
            setShowEvaluationForm(true);
        }
    };

    const getCaseImageUrl = getPatientImageUrl;

    const openSubscriptionPage = () => {
        navigate('/subscription');
    };

    return (
        <div className={`app ${!selectedCase && !sessionActive ? 'case-library-mode' : ''}`}>
            {(selectedCase || sessionActive) && (
                <Sidebar
                    cases={cases}
                    selectedCase={selectedCase}
                    onSelectCase={handleSelectCase}
                    onClose={() => setSidebarOpen(false)}
                    onClearChat={handleClearChat}
                    isOpen={sidebarOpen}
                    completedSubmissions={completedSubmissions}
                />
            )}

            <div className="main-content">
                <TopNav
                    onMenuClick={() => setSidebarOpen(true)}
                    onClearChat={handleClearChat}
                    onEvaluateClick={handleEvaluateClick}
                    onToggleAnketa={() => setShowAnketaPanel(true)}
                    onNextPhase={handleNextPhase}
                    selectedCase={selectedCase}
                    sessionActive={sessionActive}
                    sessionId={sessionId}
                    currentPhase={currentPhase}
                    currentPhaseIndex={timerPhases.findIndex(p => (sessionDuration - timer.timeLeft) < p.cumulative)}
                    totalPhases={timerPhases.length}
                    timer={timer}
                    phaseTimeRemaining={phaseTimeRemaining}
                    showLabReports={showLabReports}
                    onToggleLabReports={() => setShowLabReports(!showLabReports)}
                    onReturnToDashboard={handleReturnToDashboard}
                    onSubscriptionClick={openSubscriptionPage}
                    showMenuButton={Boolean(selectedCase || sessionActive)}
                />

                {selectedCase ? (
                    <>
                        <PatientHeader
                            selectedCase={selectedCase}
                            patientImage={patientImage}
                            onImageClick={() => setShowImageModal(true)}
                            timer={timer}
                            sessionActive={sessionActive}
                            currentPhase={currentPhase}
                            phaseTimeRemaining={phaseTimeRemaining}
                            formatPhaseTime={(ms) => {
                                const totalSeconds = Math.floor(ms / 1000);
                                const minutes = Math.floor(totalSeconds / 60);
                                const seconds = totalSeconds % 60;
                                return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                            }}
                            currentPhaseIndex={timerPhases.findIndex(p => (sessionDuration - timer.timeLeft) < p.cumulative)}
                            totalPhases={timerPhases.length}
                            onNextPhase={handleNextPhase}
                        />

                        <ChatContainer
                            messages={messages}
                            selectedCase={selectedCase}
                            isTyping={isTyping}
                        />

                        {!showEvaluationForm && !isTimerExpired && sessionActive && (
                            <ChatInput
                                inputText={inputText}
                                onInputChange={setInputText}
                                onSend={handleSend}
                                onQuickQuestion={handleQuickQuestion}
                                currentPhase={currentPhase}
                            />
                        )}
                    </>
                ) : (
                    <div className="case-selection">
                        {sessionActive ? (
                            <div className="error-state">
                                <h3>⚠️ {t('sessionActiveNoCase')}</h3>
                                <p>{t('syncProblemDetail')}</p>
                                <p>{t('sessionId')}: {sessionId || t('pendingShort')}</p>
                                <button
                                    className="btn-primary"
                                    onClick={() => {
                                        setSessionActive(false);
                                        setSessionId(null);
                                        timer.reset();
                                        window.location.reload();
                                    }}
                                >
                                    {t('resetDashboard')}
                                </button>
                            </div>
                        ) : (
                            <>
                                <section className="case-selection-hero">
                                    <div>
                                        <span className="case-kicker">{t('clinicalLibrary')}</span>
                                        <h2>
                                            <span className="headline-tool">🩺</span>
                                            {t('selectEncounter')}
                                        </h2>
                                        <p>{t('libraryIntro')}</p>
                                    </div>
                                </section>

                                {casesLoading && cases.length === 0 && (
                                    <section className="case-loading-grid" aria-label="Loading patient cases">
                                        {[1, 2, 3].map(item => (
                                            <div className="case-loading-card" key={item}>
                                                <span />
                                                <strong />
                                                <p />
                                            </div>
                                        ))}
                                    </section>
                                )}

                                <section className="case-gallery" aria-label="Available patient cases">
                                    {cases.slice(0, 6).map((caseItem) => {
                                        const completed = completedSubmissions[caseItem.id];
                                        return (
                                            <button
                                                type="button"
                                                className="case-tile"
                                                key={caseItem.id}
                                                onClick={() => handleSelectCase(caseItem)}
                                            >
                                                <div className="case-tile-image">
                                                    <img
                                                        src={getCaseImageUrl(caseItem.id)}
                                                        alt={caseItem.name}
                                                        onError={(event) => {
                                                            event.currentTarget.classList.add('is-hidden');
                                                        }}
                                                    />
                                                    <span className="case-tile-fallback">🩺</span>
                                                    <span className={completed ? 'case-tile-status complete' : 'case-tile-status'}>
                                                        {completed ? t('completed') : t('startCase')}
                                                    </span>
                                                </div>
                                                <div className="case-tile-body">
                                                    <div className="case-tile-meta">
                                                        <span>{caseItem.age} {t('years')}</span>
                                                        <span>{completed ? t('reviewAvailable') : t('timedEncounter')}</span>
                                                    </div>
                                                    <h3>{caseItem.name}</h3>
                                                    <p>{caseItem.chiefComplaint}</p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </section>
                            </>
                        )}
                    </div>
                )}

                {isTimerExpired && !showEvaluationForm && (
                    <div className="session-expired">
                        <div className="expired-message">
                            <h3>⏰ {t('sessionExpired')}</h3>
                            <p>{t('yourExamTimeEnded')}</p>
                            <button
                                className="btn-primary"
                                onClick={() => setShowEvaluationForm(true)}
                            >
                                {t('goToEvaluation')}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <EvaluationModal
                isOpen={showEvaluationForm}
                onClose={() => {
                    if (isTimerExpired && !evaluationResult) {
                        alert(t('mustCompleteEval'));
                        return;
                    }
                    setShowEvaluationForm(false);
                    if (evaluationResult) {
                        setEvaluationResult(null);
                    }
                }}
                isEvaluating={isEvaluating}
                diagnosis={diagnosis}
                managementPlan={managementPlan}
                evaluationResult={evaluationResult}
                onDiagnosisChange={setDiagnosis}
                onManagementPlanChange={setManagementPlan}
                onSubmitEvaluation={handleSubmitEvaluation}
            />

            <ImageModal
                isOpen={showImageModal}
                onClose={() => setShowImageModal(false)}
                patientImage={patientImage}
                selectedCase={selectedCase}
                imageZoom={imageZoom}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onResetZoom={resetImageZoom}
            />

            <TimerModal
                isOpen={showTimerModal}
                onClose={() => {
                    setShowTimerModal(false);
                    setTempSelectedCase(null);
                }}
                onStartSession={handleStartSession}
                patient={tempSelectedCase}
                patientImage={tempSelectedCase ? getCaseImageUrl(tempSelectedCase.id) : ''}
                phases={timerPhases.map(({ name, duration }) => ({
                    name,
                    minutes: Math.round(duration / 60000)
                }))}
                defaultPhases={DEFAULT_PHASES}
            />

            <LabReportsPanel
                isOpen={showLabReports}
                onClose={() => setShowLabReports(false)}
                caseData={selectedCase}
            />

            <AnketaPanel
                isOpen={showAnketaPanel}
                onClose={() => setShowAnketaPanel(false)}
                caseData={selectedCase}
            />

            {sidebarOpen && (
                <div className="overlay" onClick={() => setSidebarOpen(false)}></div>
            )}

            {/* Completed case modal */}
            {showCompletedModal && completedModalCase && (
                <div className="modal-overlay" onClick={() => setShowCompletedModal(false)}>
                    <div className="completed-case-modal" onClick={e => e.stopPropagation()}>
                        <h3>{t('completedCase')}</h3>
                        <p className="completed-case-name">{completedModalCase.name}</p>
                        <p className="completed-case-subtitle">{t('whatNext')}</p>
                        <div className="completed-case-actions">
                            <button className="btn-outline" onClick={handleViewTranscript}>
                                {t('viewPreviousTranscript')}
                            </button>
                            <button className="btn-primary" onClick={handleRedoCase}>
                                {t('startFresh')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transcript viewer modal */}
            {viewingTranscript && (
                <div className="modal-overlay" onClick={() => setViewingTranscript(null)}>
                    <div className="transcript-modal" onClick={e => e.stopPropagation()}>
                        <div className="transcript-modal-header">
                            <h3>{t('yourPreviousTranscript')}</h3>
                            <button className="modal-close-btn" onClick={() => setViewingTranscript(null)}>✕</button>
                        </div>
                        {viewingTranscript.system_score != null && (
                            <div className="transcript-score-row">
                                <span>{t('aiScore')}: <strong>{viewingTranscript.system_score}/100</strong></span>
                                <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                    {new Date(viewingTranscript.created_at).toLocaleString()}
                                </span>
                            </div>
                        )}
                        <div className="transcript-messages">
                            {(viewingTranscript.transcript || []).length === 0
                                ? <p className="transcript-empty">{t('noTranscriptRecorded')}</p>
                                : (viewingTranscript.transcript || []).map((msg, i) => (
                                    <div key={i} className={`transcript-msg transcript-${msg.role}`}>
                                        <span className="transcript-role">
                                            {msg.role === 'user' ? t('youStudent') : t('patient')}
                                        </span>
                                        <p>{msg.content}</p>
                                    </div>
                                ))
                            }
                        </div>
                        {viewingTranscript.student_diagnosis && (
                            <div className="transcript-written">
                                <h4>{t('yourDiagnosis')}</h4>
                                <p>{viewingTranscript.student_diagnosis}</p>
                            </div>
                        )}
                        {viewingTranscript.student_plan && (
                            <div className="transcript-written">
                                <h4>{t('yourManagementPlan')}</h4>
                                <p>{viewingTranscript.student_plan}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;
